import { useEffect, useRef, useState } from "react";
import { Input } from "@/design-system/components/ui";
import { useContactSearch } from "../hooks/useCommunications";

const DEBOUNCE_MS = 250;
const MAX_RESULTS = 8;

interface PickedContact {
  displayName: string;
  phoneOrEmail: string | null;
  contactId: string | null;
}

interface Props {
  displayName: string;
  phoneOrEmail: string;
  onPick: (picked: PickedContact) => void;
  onChangeFreeText: (name: string) => void;
  ariaLabel: string;
}

/**
 * Combobox over <c>GET /api/communications/contacts/search</c>. Free
 * text is allowed — typing without selecting from the dropdown leaves
 * the parent's <c>contactId</c> as <c>null</c>, which the backend treats
 * as a v1 free-text fallback per Comm Log Standard §9.4.
 */
export function ContactPickerInput({
  displayName,
  onPick,
  onChangeFreeText,
  ariaLabel,
}: Props) {
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const search = useContactSearch(query, MAX_RESULTS);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const scheduleSearch = (q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQuery(q), DEBOUNCE_MS);
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={displayName}
        placeholder="Name (search or type free text)"
        aria-label={ariaLabel}
        onFocus={() => {
          setOpen(true);
          if (query === null) setQuery("");
        }}
        onChange={(e) => {
          const next = e.target.value;
          onChangeFreeText(next);
          setOpen(true);
          scheduleSearch(next);
        }}
      />
      {open && (search.data?.length ?? 0) > 0 && (
        <ul
          role="listbox"
          className="absolute z-30 mt-space-1 w-full max-w-md rounded-md border border-border-light bg-white-pure shadow-lg max-h-60 overflow-y-auto"
        >
          {(search.data ?? []).map((c) => (
            <li key={c.id} role="option" aria-selected="false">
              <button
                type="button"
                className="w-full text-left px-space-3 py-space-2 hover:bg-white-warm flex flex-col"
                onClick={() => {
                  onPick({
                    displayName: c.displayName,
                    phoneOrEmail: c.email ?? c.phone ?? null,
                    contactId: c.source === "User" ? c.id : null,
                  });
                  setOpen(false);
                }}
              >
                <span className="text-small text-onyx">{c.displayName}</span>
                {(c.email || c.phone) && (
                  <span className="text-label text-gray-mid">
                    {c.email ?? c.phone}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
